<template>
  <div>
    <CDataTable
      :key="tableKey"
      :hover="false"
      :striped="true"
      :bordered="true"
      :items="data.results"
      :fields="fields"
      :column-filter="{external: true,lazy:true}"
      :column-filter-value="filters"
      :sorter="{external: true, resetable:true}"
      :loading="loading"
      :sorter-value="sorting"
      @update:column-filter-value="updateFilters"
      @update:sorter-value="updateSorting"
      :class="customClasses"
    >
      <template #no-items-view>
        <div class="text-center">
          <h5 v-translate>No items found</h5>
        </div>
      </template>
      <template #sorting-icon="{ state, classes }">
        <div :class="classes">
          <CIcon name="cipCaretUp" size="lg" v-if="state === 'asc' || state === 0"></CIcon>
          <CIcon name="cipCaretUp" size="lg" v-else></CIcon>
        </div>
      </template>
      <!-- Celle custom automatiche (boolean, etc.) -->
      <template v-for="field in customCellFields" #[field.slotName]="{ item }">
        <!-- Cella Boolean -->
        <td v-if="field.cellType === 'boolean'" :key="field.key">
          <CIcon v-if="getNestedValue(item, field.dataPath || field.key)" name="cip-check" class="text-success"/>
          <CIcon v-else name="cip-x" class="text-danger"/>
        </td>
      </template>

      <!-- Filtri custom automatici (boolean, dateRange, select) -->
      <template v-for="field in customFilterFields" #[field.filterSlot]>
        <!-- Filtro Boolean -->
        <CSelect
          v-if="field.filterType === 'boolean'"
          :key="field.key + '-bool'"
          size="sm"
          class="mb-0"
          :options="booleanOptions"
          :value.sync="filters[field.key]"
          @update:value="(val) => onBooleanFilterChange(field.key, val)"
        />
        <!-- Filtro Select con options custom -->
        <CSelect
          v-else-if="field.filterType === 'select'"
          :key="field.key + '-select'"
          size="sm"
          class="mb-0"
          :options="field.options"
          :value.sync="filters[field.key]"
          @update:value="(val) => onBooleanFilterChange(field.key, val)"
        />
        <!-- Filtro DateRange -->
        <VueCtkDateTimePicker
          v-else-if="field.filterType === 'dateRange'"
          :key="field.key + '-date'"
          v-model="dateRangeFilters[field.key]"
          :auto-close="false"
          @validate="onDateRangeFilterChange"
          @input="(val) => onDateRangeInput(val, field)"
          range
          :no-button="false"
          :no-shortcuts="false"
          input-size="sm"
          position="bottom"
          :no-label="true"
          :only-date="true"
          format="YYYY-MM-DD"
          formatted="L"
          :right="false"
          :no-header="true"
          :no-button-now="false"
        />
      </template>

      <!-- Pass-through di tutti gli slot dal parent -->
      <template v-for="(slot, name) in $scopedSlots" v-slot:[name]="slotData">
        <slot :name="name" v-bind="slotData"></slot>
      </template>
    </CDataTable>
    <CPagination
      class="mt-3"
      align="center"
      v-show="data.pagination.num_pages > 1"
      :activePage="data.pagination.page"
      @update:activePage="updatePage"
      :pages="data.pagination.num_pages"
    />
  </div>
</template>

<script>
/**
 * AjaxTable - Componente tabella con paginazione, sorting e filtri automatici
 *
 * Props:
 *   - fields: Array di definizioni colonne
 *   - data: Object con { results: [], pagination: { page, num_pages, page_size, total } }
 *   - fetch: Function({ filters, pagination, sorting }) - chiamata per caricare i dati
 *
 * Definizione Fields:
 *   {
 *     key: 'column_name',           // Chiave della colonna (obbligatorio)
 *     label: 'Label',               // Etichetta header
 *     sorter: true,                 // Abilita sorting su questa colonna
 *     filter: false,                // Disabilita qualsiasi filtro (default: true)
 *     options: [{label, value}],    // Filtro select con opzioni custom
 *     boolean: true,                // Cella con ✓/✗ + filtro select Sì/No/Qualsiasi (se filter !== false)
 *     dataPath: 'nested.path',      // Path per valore nested (es. 'event.is_draft')
 *     dateRangeFrom: 'param_from',  // Filtro date range - parametro "da"
 *     dateRangeTo: 'param_to',      // Filtro date range - parametro "a"
 *     defaultValue: 'value',        // Valore di default per il filtro
 *     _style: 'width:100px',        // Stile inline colonna
 *     _classes: 'sticky-column',    // Classi CSS colonna
 *   }
 *
 * Celle custom automatiche (customCellFields):
 *   - boolean: true → Mostra ✓/✗ (sovrascrivibile con slot custom)
 *
 * Filtri custom automatici (customFilterFields) - rispettano filter: false:
 *   - boolean: true → Select con Qualsiasi/Sì/No
 *   - options: [...] → Select con opzioni custom
 *   - dateRangeFrom/dateRangeTo → Date picker con range
 *
 * Esempi:
 *   // Colonna con sorting
 *   {key: 'name', label: 'Nome', sorter: true}
 *
 *   // Colonna boolean (filtro select + cella ✓/✗)
 *   {key: 'is_active', label: 'Attivo', boolean: true, sorter: true}
 *
 *   // Colonna boolean senza filtro (solo cella ✓/✗)
 *   {key: 'is_promo', label: 'Promo', boolean: true, filter: false, sorter: true}
 *
 *   // Colonna con filtro select custom
 *   {key: 'status', label: 'Stato', options: [{label: 'Attivo', value: '1'}]}
 *
 *   // Colonna con filtro date range
 *   {key: 'created_at', label: 'Data', dateRangeFrom: 'date_from', dateRangeTo: 'date_to', sorter: true}
 *
 * Metodi esposti via ref:
 *   - fetchList(): Ricarica i dati
 *   - hasActiveFilters(): Ritorna true se ci sono filtri attivi
 */
export default {
  name: "AjaxTable",
  props: {
    fields: {type: Array},
    data: {type: Object},
    fetch: {type: Function},
    customClasses: {type: String, default: 'custom-table custom-table--has-filters custom-table--clean'},
  },
  emits: [],
  data() {
    return {
      pagination: {
        page_size: 20,
        page: 1,
      },
      sorting: {
        column: null,
        asc: false
      },
      filters: {},
      dateRangeFilters: {},
      loading: true,
    }
  },
  computed: {
    customFilterFields() {
      // Tutti i fields con filtri custom (boolean, select, dateRange)
      // Rispetta filter: false per non mostrare il filtro
      // Ogni field ha: filterSlot, filterType
      if (!this.fields) return []
      const fields = []

      this.fields.forEach(field => {
        // Se filter è esplicitamente false, non mostrare filtro
        if (field.filter === false) return

        if (field.boolean === true) {
          fields.push({...field, filterSlot: field.key + '-filter', filterType: 'boolean'})
        } else if (field.options && Array.isArray(field.options)) {
          fields.push({...field, filterSlot: field.key + '-filter', filterType: 'select'})
        } else if (field.dateRangeFrom || field.dateRangeTo) {
          fields.push({...field, filterSlot: field.key + '-filter', filterType: 'dateRange'})
        }
      })

      return fields
    },
    customCellFields() {
      // Fields con celle custom (boolean, etc.) che non hanno slot sovrascritto dal parent
      // Ogni field ha: slotName, cellType
      if (!this.fields) return []
      const fields = []

      this.fields.forEach(field => {
        // Skip se il parent ha già definito uno slot per questa colonna
        if (this.$scopedSlots[field.key]) return

        if (field.boolean === true) {
          fields.push({...field, slotName: field.key, cellType: 'boolean'})
        }
        // Qui si possono aggiungere altri tipi di celle custom in futuro
      })

      return fields
    },
    booleanOptions() {
      return [
        {value: null, label: this.$gettext('Any')},
        {value: 'true', label: this.$gettext('Yes')},
        {value: 'false', label: this.$gettext('No')},
      ]
    },
    tableKey() {
      // Forza re-render quando cambiano i fields
      return this.fields ? this.fields.length : 0
    },
    // Merge standard filters with date range filters for the fetch call
    mergedFilters() {
      const merged = {...this.filters}
      // Add date range filters
      const dateRangeFields = this.customFilterFields.filter(f => f.filterType === 'dateRange')
      for (const field of dateRangeFields) {
        const dateRange = this.dateRangeFilters[field.key]
        if (dateRange && dateRange.start) {
          merged[field.dateRangeFrom || `${field.key}_from`] = dateRange.start
        }
        if (dateRange && dateRange.end) {
          merged[field.dateRangeTo || `${field.key}_to`] = dateRange.end
        }
      }
      return merged
    },
  },
  mounted() {
    this.initializeDefaultFilters()
    this.fetchList()
  },
  methods: {
    initializeDefaultFilters() {
      // Legge i valori di default dai fields con defaultValue
      if (!this.fields) return

      const defaultFilters = {}
      this.fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          defaultFilters[field.key] = field.defaultValue
        }
      })

      this.filters = {...defaultFilters, ...this.filters}
    },
    async fetchList() {
      this.loading = true
      await this.fetch({
        filters: this.mergedFilters,
        pagination: this.pagination,
        sorting: this.sorting
      })
      this.pagination.page = this.data.pagination.page
      this.loading = false
    },
    updateFilters(filters) {
      this.filters = filters
      this.pagination.page = 1
      this.fetchList()
    },
    updateSorting(sorting) {
      this.sorting = sorting
      this.fetchList()
    },
    updatePage(page) {
      this.pagination.page = page
      this.fetchList()
    },
    onBooleanFilterChange(key, val) {
      this.$set(this.filters, key, val)
      this.pagination.page = 1
      this.fetchList()
    },
    onDateRangeFilterChange() {
      // Quando viene confermato un filtro dateRange, aggiorna la lista
      this.pagination.page = 1
      this.fetchList()
    },
    onDateRangeInput(value, field) {
      // Quando viene cancellato il valore del date range
      if (!value) {
        this.$set(this.dateRangeFilters, field.key, {})
        this.pagination.page = 1
        this.fetchList()
      }
    },
    hasActiveFilters() {
      // Controlla se ci sono filtri attivi (select, text o date range)
      const hasTextOrSelectFilters = Object.values(this.filters).some(v => v !== '' && v !== null && v !== undefined)
      const hasDateRangeFilters = Object.values(this.dateRangeFilters).some(v => v && (v.start || v.end))
      return hasTextOrSelectFilters || hasDateRangeFilters
    },
    getNestedValue(obj, path) {
      // Accede a valori nested es. 'event.is_draft' → obj.event.is_draft
      return path.split('.').reduce((o, k) => (o || {})[k], obj)
    }
  }
}
</script>